import React from "react";
import {
  FaArrowDown,
  FaArrowUp,
  FaEdit,
  FaEnvelope,
  FaPhone,
  FaTrash,
  FaUserFriends,
} from "react-icons/fa";

import { toAbsolutePhotoUrl } from "../../../../../../../services/teamPhotoServices";

import { TEAM_DEFAULT_AVATAR } from "../utils/teamSettingsUtils";

function ActionButton({ title, onClick, disabled, children, tone = "default" }) {
  const tones = {
    default:
      "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
    primary:
      "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    danger:
      "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border transition disabled:cursor-not-allowed disabled:opacity-50 ${
        tones[tone] || tones.default
      }`}
    >
      {children}
    </button>
  );
}

function SkeletonRows() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="space-y-3 p-4 sm:p-5 lg:p-6">
        {[...Array(5)].map((_, index) => (
          <div
            key={index}
            className="h-20 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[24px] bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          <FaUserFriends />
        </div>

        <h3 className="mt-5 text-lg font-bold text-slate-900 dark:text-white">
          Aucun membre
        </h3>

        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Ajoutez un membre ou modifiez vos filtres.
        </p>
      </div>
    </section>
  );
}

export default function TeamTable({
  team,
  loading,
  deletingId,
  reordering,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
}) {
  if (loading) return <SkeletonRows />;
  if (!team.length) return <EmptyState />;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-4">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            Liste
          </p>

          <h3 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
            Membres enregistrés
          </h3>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Utilisez les flèches pour modifier l’ordre d’affichage public. Sur
            petits écrans, le tableau glisse horizontalement.
          </p>
        </div>

        <div className="overflow-x-auto rounded-[22px] border border-slate-200 dark:border-slate-800">
          <table className="w-full min-w-[1120px]">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-500 dark:bg-slate-950/70 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left font-bold">Ordre</th>
                <th className="px-4 py-3 text-left font-bold">Photo</th>
                <th className="px-4 py-3 text-left font-bold">Membre</th>
                <th className="px-4 py-3 text-left font-bold">Fonction</th>
                <th className="px-4 py-3 text-left font-bold">Catégorie</th>
                <th className="px-4 py-3 text-left font-bold">Contact</th>
                <th className="sticky right-0 z-20 border-l border-slate-200 bg-slate-50 px-4 py-3 text-right font-bold dark:border-slate-700 dark:bg-slate-950">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
              {team.map((member, index) => {
                const photoUrl =
                  toAbsolutePhotoUrl(member.photoURL) || TEAM_DEFAULT_AVATAR;

                return (
                  <tr
                    key={member.id}
                    className="transition hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <td className="px-4 py-4 align-top">
                      <div className="flex items-center gap-2">
                        <span className="min-w-8 rounded-xl bg-slate-100 px-2 py-1 text-center text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                          {member.position || index + 1}
                        </span>

                        <div className="flex flex-col">
                          <button
                            type="button"
                            onClick={() => onMoveUp(member)}
                            disabled={index === 0 || reordering}
                            className="rounded-md p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-slate-800 dark:hover:text-white"
                            title="Monter"
                          >
                            <FaArrowUp className="text-[11px]" />
                          </button>

                          <button
                            type="button"
                            onClick={() => onMoveDown(member)}
                            disabled={index === team.length - 1 || reordering}
                            className="rounded-md p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-slate-800 dark:hover:text-white"
                            title="Descendre"
                          >
                            <FaArrowDown className="text-[11px]" />
                          </button>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-4 align-top">
                      <button
                        type="button"
                        onClick={() => onEdit(member)}
                        className="group relative h-16 w-16 overflow-hidden rounded-2xl bg-slate-100 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700"
                        title="Modifier le membre"
                      >
                        <img
                          src={photoUrl}
                          alt={`${member.prenom || ""} ${member.nom || ""}`}
                          className="h-full w-full object-cover"
                          referrerPolicy="no-referrer"
                          crossOrigin="anonymous"
                          onError={(event) => {
                            event.currentTarget.src = TEAM_DEFAULT_AVATAR;
                          }}
                        />

                        <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-slate-950/0 text-[10px] font-bold text-white opacity-0 transition group-hover:bg-slate-950/55 group-hover:opacity-100">
                          Modifier
                        </span>
                      </button>
                    </td>

                    <td className="px-4 py-4 align-top">
                      <p className="font-bold text-slate-900 dark:text-white">
                        {member.prenom || ""} {member.nom || ""}
                      </p>

                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        ID : {member.id}
                      </p>
                    </td>

                    <td className="max-w-[260px] px-4 py-4 align-top text-slate-700 dark:text-slate-200">
                      <p className="line-clamp-2">
                        {member.fonction || "Fonction non renseignée"}
                      </p>
                    </td>

                    <td className="px-4 py-4 align-top">
                      <span className="inline-flex rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20">
                        {member.categorie || "—"}
                      </span>
                    </td>

                    <td className="px-4 py-4 align-top">
                      <div className="space-y-1.5 text-xs text-slate-600 dark:text-slate-300">
                        {member.mail_member ? (
                          <p className="flex items-center gap-2">
                            <FaEnvelope className="text-slate-400" />
                            <span className="max-w-[220px] truncate">
                              {member.mail_member}
                            </span>
                          </p>
                        ) : null}

                        {member.tel_member ? (
                          <p className="flex items-center gap-2">
                            <FaPhone className="text-slate-400" />
                            <span>{member.tel_member}</span>
                          </p>
                        ) : null}

                        {!member.mail_member && !member.tel_member ? (
                          <span className="text-slate-400">
                            Aucune coordonnée
                          </span>
                        ) : null}
                      </div>
                    </td>

                    <td className="sticky right-0 z-10 border-l border-slate-200 bg-white px-4 py-4 align-top dark:border-slate-700 dark:bg-slate-900">
                      <div className="flex items-center justify-end gap-2">
                        <ActionButton
                          title="Modifier"
                          onClick={() => onEdit(member)}
                          tone="primary"
                        >
                          <FaEdit />
                        </ActionButton>

                        <ActionButton
                          title="Supprimer"
                          onClick={() => onDelete(member)}
                          disabled={deletingId === member.id}
                          tone="danger"
                        >
                          <FaTrash />
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}